import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import API_BASE_URL from '../config/api';
import { useTheme } from '../utils/ThemeContext';
import { 
  Box, 
  Container, 
  Paper, 
  TextField, 
  Button, 
  Typography, 
  Alert,
  IconButton,
  InputAdornment,
  Snackbar
} from '@mui/material';
import { 
  Visibility, 
  VisibilityOff, 
  Email as EmailIcon,
  Lock as LockIcon,
  PersonAdd as PersonAddIcon
} from '@mui/icons-material';
import axios from 'axios';
import '../assets/css/futuristic-modern.css';

// Add custom styles for theme-aware inputs
const themeInputStyles = {
  '& .MuiOutlinedInput-root': {
    color: 'var(--text-primary) !important',
    backgroundColor: 'var(--surface) !important',
    '& fieldset': {
      borderColor: 'var(--border) !important',
    },
    '&:hover fieldset': {
      borderColor: 'var(--neon-cyan) !important',
    },
    '&.Mui-focused fieldset': {
      borderColor: 'var(--neon-cyan) !important',
    },
    '& input': {
      color: 'var(--text-primary) !important',
      backgroundColor: 'transparent !important',
    },
    '& .MuiInputAdornment-root': {
      color: 'var(--text-muted) !important',
    },
  },
  '& .MuiInputLabel-root': {
    color: 'var(--text-muted) !important',
    '&.Mui-focused': {
      color: 'var(--neon-cyan) !important',
    },
  },
};

const SignUp = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();

  // Snackbar notification state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  const showNotification = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const handleCloseSnackbar = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar({ ...snackbar, open: false });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMessage('');

    // Validate passwords match
    if (password !== confirmPassword) {
      setErrorMessage('Passwords do not match');
      setLoading(false);
      return;
    }

    // Validate password strength
    if (password.length < 6) {
      setErrorMessage('Password must be at least 6 characters long');
      setLoading(false);
      return;
    }

    try {
        await axios.post(`${API_BASE_URL}/api/signup`, { email, password });
      showNotification('Sign up successful! Please check your email to verify your account.', 'success');
      setTimeout(() => {
        navigate('/signin');
      }, 1500); // Give user time to see the success message
    } catch (error) {
      if (error.response) {
        setErrorMessage(error.response.data.error || 'Sign up failed');
      } else if (error.request) {
        setErrorMessage('No response received from server. Please try again.');
      } else {
        setErrorMessage('An unexpected error occurred. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleToggleConfirmPasswordVisibility = () => {
    setShowConfirmPassword(!showConfirmPassword);
  };

  return (
    <Box 
      sx={{ 
        minHeight: '100vh',
        background: 'var(--surface)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 2
      }}
    >
      <Container maxWidth="sm">
        <Paper 
          elevation={24}
          sx={{
            background: 'var(--surface-2)',
            border: '1px solid var(--glass-border)',
            borderRadius: 'var(--radius-lg)',
            backdropFilter: 'blur(20px)',
            padding: { xs: 3, md: 4 },
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              height: '4px',
              background: 'var(--accent-gradient)',
            }
          }}
        >
          {/* Header */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2 }}>
              <img
                src={isDarkMode ? "/aletheon-logo-white.png" : "/aletheon-logo-black.png"}
                alt="Aletheon"
                style={{
                  height: '80px',
                  width: 'auto',
                  maxWidth: '400px'
                }}
              />
            </Box>
            <Typography 
              variant="body1" 
              sx={{ 
                color: 'var(--text-primary)',
                fontSize: '1.1rem'
              }}
            >
              Create your account to start your research journey
            </Typography>
          </Box>

          {/* Error Alert */}
          {errorMessage && (
            <Alert 
              severity="error" 
              sx={{ 
                mb: 3,
                background: 'rgba(244, 67, 54, 0.1)',
                border: '1px solid rgba(244, 67, 54, 0.3)',
                color: '#ff6b6b'
              }}
            >
              {errorMessage}
            </Alert>
          )}

          {/* Sign Up Form */}
          <Box component="form" onSubmit={handleSubmit} sx={{ mb: 3 }}>
            <TextField
              fullWidth
              label="Email Address"
                          type="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)} 
                          required 
              variant="outlined"
              sx={{
                mb: 3,
                ...themeInputStyles
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <EmailIcon sx={{ color: 'var(--text-muted)' }} />
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Password"
              type={showPassword ? 'text' : 'password'}
                          value={password}
                          onChange={(e) => setPassword(e.target.value)} 
                          required 
              variant="outlined"
              sx={{
                mb: 3,
                ...themeInputStyles
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon sx={{ color: 'var(--text-muted)' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleTogglePasswordVisibility}
                      edge="end"
                      sx={{ color: 'var(--text-muted)' }}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              fullWidth
              label="Confirm Password"
              type={showConfirmPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              required
              variant="outlined"
              sx={{
                mb: 3,
                ...themeInputStyles
              }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <LockIcon sx={{ color: 'var(--text-muted)' }} />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={handleToggleConfirmPasswordVisibility}
                      edge="end"
                      sx={{ color: 'var(--text-muted)' }}
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              disabled={loading}
              startIcon={<PersonAddIcon />}
              sx={{
                background: 'var(--accent-gradient)',
                color: '#ffffff !important',
                padding: '12px',
                fontSize: '1.1rem',
                fontWeight: 600,
                borderRadius: 'var(--radius-md)',
                textTransform: 'none',
                boxShadow: 'none',
                '& .MuiSvgIcon-root': {
                  color: '#ffffff !important',
                },
                '& *': {
                  color: '#ffffff !important',
                },
                '&:hover': {
                  background: 'var(--accent-gradient)',
                  boxShadow: 'none',
                  transform: 'none',
                },
                '&:disabled': {
                  background: 'var(--accent-gradient)',
                  color: '#ffffff !important',
                  opacity: 0.7,
                },
                transition: 'all 0.3s ease-in-out',
              }}
            >
              {loading ? 'Creating Account...' : 'Create Account'}
            </Button>
          </Box>

          {/* Sign In Link */}
          <Box sx={{ textAlign: 'center' }}>
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'var(--text-primary)',
                mb: 1
              }}
            >
              Already have an account?
            </Typography>
            <Link 
              to="/signin"
              style={{
                color: 'var(--neon-cyan)',
                textDecoration: 'none',
                fontWeight: 600,
                fontSize: '1rem',
                transition: 'all 0.3s ease-in-out',
              }}
              onMouseEnter={(e) => {
                e.target.style.color = 'var(--text-primary)';
                e.target.style.textShadow = '0 0 10px var(--neon-cyan)';
              }}
              onMouseLeave={(e) => {
                e.target.style.color = 'var(--neon-cyan)';
                e.target.style.textShadow = 'none';
              }}
            >
              Sign in to your account
            </Link>
          </Box>
        </Paper>

        {/* Footer */}
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <Typography 
            variant="body2" 
            sx={{ 
              color: 'var(--text-primary)',
              opacity: 0.7
            }}
          >
            © 2024 Aletheon - AI Research Platform
          </Typography>
        </Box>
      </Container>
      {/* Styled Snackbar for Notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          variant="filled"
          sx={{
            width: '100%',
            backgroundColor: snackbar.severity === 'warning' 
              ? 'rgba(255, 152, 0, 0.9)' 
              : snackbar.severity === 'error'
              ? 'rgba(244, 67, 54, 0.9)'
              : snackbar.severity === 'success'
              ? 'rgba(70, 130, 48, 0.9)'
              : 'rgba(108, 48, 130, 0.9)',
            color: '#ffffff',
            border: snackbar.severity === 'warning'
              ? '1px solid rgba(255, 152, 0, 0.5)'
              : snackbar.severity === 'error'
              ? '1px solid rgba(244, 67, 54, 0.5)'
              : snackbar.severity === 'success'
              ? '1px solid rgba(70, 130, 48, 0.5)'
              : '1px solid rgba(108, 48, 130, 0.5)',
            borderRadius: 'var(--radius-md)',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)',
            '& .MuiAlert-icon': {
              color: '#ffffff'
            }
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SignUp;
